import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { adminApi } from "../services/api";
import { useI18n, useMoneyFormatter } from "@expertloop/i18n/react";

type MediationFilter = "all" | "pending" | "resolved" | "none";

const MEDIATION_FILTERS: MediationFilter[] = ["all", "pending", "resolved", "none"];

/** Délai laissé à la frappe avant d'interroger le serveur. */
const SEARCH_DELAY_MS = 300;

interface ChatSummary {
	id: string;
	clientEmail: string;
	clientName?: string;
	status: string;
	/** État de la médiation de la dernière commande. */
	mediationState?: Exclude<MediationFilter, "all">;
	createdAt: string;
	expert: {
		id: string;
		email: string;
		displayName?: string;
		firstName: string;
		lastName: string;
	};
	product: {
		id: string;
		name: string;
		price: number;
		currency: string;
	};
	order?: {
		amount: number;
		status: string;
		hasMediation: boolean;
	};
}

interface ChatsResponse {
	chats: ChatSummary[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

export function ChatsPage() {
	const { t } = useI18n();
	const { formatUnits } = useMoneyFormatter();
	const [chats, setChats] = useState<ChatSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [page, setPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [total, setTotal] = useState(0);
	const [status, setStatus] = useState("all");
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const initialMediation = searchParams.get("mediation");
	const [mediation, setMediation] = useState<MediationFilter>(
		MEDIATION_FILTERS.includes(initialMediation as MediationFilter) ? (initialMediation as MediationFilter) : "all",
	);
	const [search, setSearch] = useState("");
	const [searchInput, setSearchInput] = useState("");

	// La liste suit la saisie : la recherche part peu après la dernière frappe
	useEffect(() => {
		const trimmed = searchInput.trim();
		if (trimmed === search) return;
		const timer = setTimeout(() => {
			setSearch(trimmed);
			setPage(1);
		}, SEARCH_DELAY_MS);
		return () => clearTimeout(timer);
	}, [searchInput, search]);

	useEffect(() => {
		let cancelled = false;
		const fetchChats = async () => {
			setLoading(true);
			try {
				const params = new URLSearchParams({
					page: page.toString(),
					pageSize: "20",
					status,
					mediation,
				});
				if (search) params.append("search", search);

				const data = await adminApi.get<ChatsResponse>(
					`/admin/chats?${params}`,
				);
				// Une réponse arrivée après une saisie plus récente est ignorée
				if (cancelled) return;
				setChats(data.chats);
				setTotalPages(data.totalPages);
				setTotal(data.total);
				setError("");
			} catch (err) {
				if (cancelled) return;
				setError(err instanceof Error ? err.message : t("chatsPage.loadFailed"));
			} finally {
				if (!cancelled) setLoading(false);
			}
		};

		fetchChats();
		return () => {
			cancelled = true;
		};
	}, [page, status, mediation, search]);

	const formatDate = (date: string) => {
		return new Date(date).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	return (
		<div>
			<div className="page-header">
				<h1>{t('adminNav.allChats')}</h1>
				<p>{t('adminChatsPage.totalChats', { count: total })}</p>
			</div>

			<div className="filters-bar">
				<div style={{ flex: 1, display: "flex", gap: "0.5rem" }}>
					<input
						type="search"
						className="search-input"
						placeholder={t('adminChatsPage.searchPlaceholder')}
						aria-label={t('adminChatsPage.searchPlaceholder')}
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
					/>
					{searchInput && (
						<button type="button" className="btn btn-outline btn-sm" onClick={() => setSearchInput("")}>
							{t('adminChatsPage.clearSearch')}
						</button>
					)}
				</div>
				<select
					className="filter-select"
					aria-label={t('adminChatsPage.mediationFilterLabel')}
					value={mediation}
					onChange={(e) => {
						setMediation(e.target.value as MediationFilter);
						setPage(1);
					}}
				>
					<option value="all">{t('adminChatsPage.mediationAll')}</option>
					<option value="pending">{t('adminChatsPage.mediationPending')}</option>
					<option value="resolved">{t('adminChatsPage.mediationResolvedFilter')}</option>
					<option value="none">{t('adminChatsPage.mediationNone')}</option>
				</select>
				<select
					className="filter-select"
					aria-label={t('adminChatsPage.status')}
					value={status}
					onChange={(e) => {
						setStatus(e.target.value);
						setPage(1);
					}}
				>
					<option value="all">{t('adminChatsPage.allStatus')}</option>
					<option value="pending">{t('adminChatsPage.pending')}</option>
					<option value="accepted">{t('adminChatsPage.accepted')}</option>
					<option value="declined">{t('adminChatsPage.declined')}</option>
					<option value="completed">{t('chatsPage.filter.completed')}</option>
					<option value="abandoned">{t('adminChatsPage.abandoned')}</option>
				</select>
			</div>

			{error && <div className="error-message">{error}</div>}

			<div className="card">
				{loading && chats.length === 0 ? (
					<div className="loading">{t('chatsPage.loading')}</div>
				) : chats.length === 0 ? (
					<div className="empty-state">
						<h3>{t('adminChatsPage.emptyTitle')}</h3>
						<p>{t('adminChatsPage.emptyBody')}</p>
					</div>
				) : (
					<div className="table-container">
						<table>
							<thead>
								<tr>
									<th>{t('adminMediations.client')}</th>
									<th>{t('adminMediations.expert')}</th>
									<th>{t('adminChatsPage.product')}</th>
									<th>{t('adminChatsPage.status')}</th>
									<th>{t('adminChatsPage.order')}</th>
									<th>{t('adminChatsPage.created')}</th>
									<th>{t('adminChatsPage.actions')}</th>
								</tr>
							</thead>
							<tbody>
								{chats.map((chat) => (
									<tr key={chat.id} className="clickable-row" onClick={() => navigate(`/chats/${chat.id}`)}>
										<td>
											<div>{chat.clientName || t('adminChatsPage.guest')}</div>
											<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
												{chat.clientEmail}
											</div>
										</td>
										<td>
											<div>
												{chat.expert.displayName ||
													`${chat.expert.firstName} ${chat.expert.lastName}`}
											</div>
											<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
												{chat.expert.email}
											</div>
										</td>
										<td>
											<div>{chat.product.name}</div>
											<div style={{ fontSize: "0.75rem", color: "var(--color-gray-500)" }}>
												{formatUnits(chat.product.price, chat.product.currency)}
											</div>
										</td>
										<td>
											<span className={`status-badge ${chat.status}`}>
												{chat.status}
											</span>
										</td>
										<td>
											{chat.order ? (
												<div>
													<span className={`status-badge ${chat.order.status}`}>
														{chat.order.status}
													</span>
													{chat.mediationState && chat.mediationState !== "none" && (
														<span
															className={`status-badge ${chat.mediationState === "pending" ? "mediation" : "completed"}`}
															style={{ marginLeft: "0.25rem" }}
														>
															{chat.mediationState === "pending"
																? t('adminChatsPage.mediationPending')
																: t('adminChatsPage.mediationResolved')}
														</span>
													)}
												</div>
											) : (
												<span style={{ color: "var(--color-gray-400)" }}>{t('adminChatsPage.noOrder')}</span>
											)}
										</td>
										<td>{formatDate(chat.createdAt)}</td>
										<td>
											<Link to={`/chats/${chat.id}`} className="table-link">
												{t('chatsPage.view')}
											</Link>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>

			{totalPages > 1 && (
				<div className="pagination">
					<button
						className="pagination-btn"
						onClick={() => setPage((p) => Math.max(1, p - 1))}
						disabled={page === 1}
					>
						{t('common.previous')}
					</button>
					<span className="pagination-info">
						{t('common.pageOf', { page, totalPages })}
					</span>
					<button
						className="pagination-btn"
						onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
						disabled={page === totalPages}
					>
						{t('common.next')}
					</button>
				</div>
			)}
		</div>
	);
}
